import React, { SetStateAction, useState } from 'react'
import { IconS24User24 } from '@is24/cosma-ui-icons'
import { InsertionFlowScreensProps } from '../../../../../@types/props-types'
import { SCREEN_CONTACT_INFO, SCREENS } from '../../../../../services/domain/wizardScreens'
import { ContactInfoScreenValidationResult } from '../../../../../@types/validation-types'
import { isEditEntryMode, OnFieldChangeType, TooltipTypes } from '../../../../../@types/types'
import ScreenContainer from '../../../../common/containers/screenContainer/ScreenContainer'
import { ScreenLayout } from '../../../../common/containers/screenLayout/ScreenLayout'
import ContactInfoManager from './ContactInfoManager'
import ReadOnlyContactInfo from './readOnlyContactInfo/ReadOnlyContactInfo'
import ContactInfoFields from './ContactInfoFields'
import { defaultPageIntersectionOptions } from '../withNavigateOnInView'
import { ScreenTooltip } from '../../../../common/display/screenTooltip/ScreenTooltip'
import { isWebView } from '../../../../../utils/navigation'
import { InitState } from '../../../../../redux/reducers/root/initialState'
import { useAppSelector } from '../../../../../app/hooks'

const screen = SCREENS[SCREEN_CONTACT_INFO]

export type ContactInfoProps = {
  hasContactManager?: boolean
  onChange: OnFieldChangeType
}

const ContactInfo: React.FC<InsertionFlowScreensProps<ContactInfoScreenValidationResult> & ContactInfoProps> = ({
  hasContactManager = false,
  insertionData,
  validationMessages,
  onChange,
  onNavigate,
}) => {
  const entryMode = useAppSelector(
    (state: InitState) => state.insertionFeature.insertionIdentification.metaInformation?.entryMode
  )

  const isPublished = insertionData.published === true

  const [isReadOnlyMode, setIsReadOnlyMode] = useState(
    validationMessages?.salutation?.length === 0 &&
      validationMessages?.firstName?.length === 0 &&
      validationMessages?.lastName?.length === 0 &&
      validationMessages?.phoneNumber?.length === 0
  )

  const displayAppropriateTooltip = (tooltipType: TooltipTypes) => (
    <ScreenTooltip tooltipType={tooltipType} tooltipKey='tooltip.screens.contactInfo.common' />
  )
  const handleReadOnlyModeClick = (value: SetStateAction<boolean>) => {
    setIsReadOnlyMode(value)
  }
  const editEntryMode = isEditEntryMode(entryMode)

  return (
    <ScreenContainer
      containerId={screen.id}
      screen={screen}
      className={isWebView() ? 'margin-bottom-xxl' : 'fixed-last-section-margin'}
      disableMarginBottom={true}
      onNavigate={onNavigate}
      intersectionOptions={
        isReadOnlyMode
          ? { ...defaultPageIntersectionOptions, rootMargin: '-5% 0% -25% 0%', threshold: 1 }
          : { ...defaultPageIntersectionOptions }
      }
    >
      {hasContactManager ? (
        <ScreenLayout
          id={`${screen.id}-desk-tooltip`}
          headline={'wizard.screen.contactInfo.header'}
          headerIcon={<IconS24User24 />}
          screenName={screen.name}
        >
          <ContactInfoManager selectedContactId={insertionData.contactId} onChange={onChange} />
        </ScreenLayout>
      ) : (
        <ScreenLayout
          id={`${screen.id}-desk-tooltip`}
          headline={'wizard.screen.contactInfo.header'}
          deskTooltip={isReadOnlyMode ? undefined : displayAppropriateTooltip(TooltipTypes.DESKTOP)}
          mobileTooltip={
            isWebView() ? undefined : isReadOnlyMode ? undefined : displayAppropriateTooltip(TooltipTypes.MOBILE)
          }
          screenName={screen.name}
          headerIcon={<IconS24User24 />}
          className='grid grid-flex gutter-vertical-l'
        >
          {!isWebView() && isReadOnlyMode ? (
            <ReadOnlyContactInfo setIsReadOnlyMode={handleReadOnlyModeClick} onChange={onChange} />
          ) : (
            <ContactInfoFields
              isReadOnlyMode={editEntryMode || isPublished}
              screenName={screen.name}
              insertionData={insertionData}
              validationMessages={validationMessages}
              onChange={onChange}
              onNavigate={onNavigate}
            />
          )}
        </ScreenLayout>
      )}
    </ScreenContainer>
  )
}

export default ContactInfo
